// src/pages/NewsStat.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
} from '@mui/material';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

function NewsStat() {
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [bulletinsData, setBulletinsData] = useState([]);
  const [filteredBulletins, setFilteredBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'newsStat', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le tri et le filtre
            publicationDate: item.publication_date ? new Date(item.publication_date) : null,
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setBulletinsData(sorted);
          setFilteredBulletins(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);

    let filtered;
    if (year === 'Tous') {
      filtered = [...bulletinsData];
    } else {
      filtered = bulletinsData.filter((bulletin) => {
        const bulletinYear = bulletin.date.getFullYear();
        return bulletinYear === parseInt(year);
      });
    }
    // Trier par date décroissante
    filtered.sort((a, b) => b.date - a.date);
    setFilteredBulletins(filtered);
    setVisibleCount(4); // réinitialiser l'affichage
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      secondary: { main: '#673AB7' },
      background: { default: '#f5f5f5' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h6: {
        fontWeight: '500',
        fontSize: '1rem',
        textTransform: 'none',
        color: '#3B3F54',
      },
      body2: {
        fontSize: 14,
        letterSpacing: 0.25,
        color: '#6B778C',
      },
      body1: {
        fontSize: 16,
        letterSpacing: 0.5,
        color: '#6B778C',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: '500',
            padding: '10px 20px',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0px 12px 20px rgba(0, 0, 0, 0.15)',
            },
            borderRadius: '8px',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h6: { fontWeight: '500', fontSize: '1rem' },
          h4: { fontWeight: '600' },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: { marginBottom: '16px' },
        },
      },
    },
  });

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = bulletinsData.map((bulletin) => bulletin.date.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [bulletinsData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              NewsStat
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              NewsStat
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
            NewsStat
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Bienvenue sur la page NewsStat, votre source essentielle pour accéder aux dernières actualités et analyses statistiques concernant le Burundi.
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Filtrer par année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Filtrer par année de publication"
            >
              <MenuItem value="Tous">Tous les NewsStats</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '5px' }}>
            {filteredBulletins.slice(0, visibleCount).map((bulletin, index) => (
              <Grid item xs={12} sm={6} md={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <CardMedia
                      component="img"
                      sx={{ width: 151, height: 200, m: 2 }}
                      image={bulletin.image}
                      alt={bulletin.title}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                        <a
                          href={bulletin.link}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bulletin.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {bulletin.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(bulletin.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredBulletins.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de NewsStat
            </Button>
          )}
          <br />
          <br />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            <strong>Note :</strong> Les <strong>NewsStats de l'INSBU</strong> représentent une source essentielle d'information statistique pour le Burundi. Ces bulletins sont publiés trimestriellement, généralement <strong>45 jours</strong> après la clôture de chaque trimestre, permettant ainsi d'assurer une diffusion rapide et efficace des données pertinentes.
            <br /><br />
            <strong>Calendrier de publication :</strong> Les dates prévues pour la publication de ces NewsStats sont les suivantes :
            <ul style={{ listStyleType: 'circle', margin: '10px 0 10px 20px', padding: 0 }}>
              <li><strong>15 février</strong> - Publication des données pour le quatrième trimestre de l'année précédente.</li>
              <li><strong>15 mai</strong> - Publication des données pour le premier trimestre de l'année en cours.</li>
              <li><strong>15 août</strong> - Publication des données pour le deuxième trimestre de l'année en cours.</li>
              <li><strong>15 novembre</strong> - Publication des données pour le troisième trimestre de l'année en cours.</li>
            </ul>
          </Typography>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default NewsStat;